import { apiRequest } from './client';
import { getApiToken, tryGetApiToken } from './authToken';
import { toJsonSafe } from './json';

export function newRecordId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function collectionPath(companyId, kind, id) {
  const base = `/api/companies/${encodeURIComponent(companyId)}/${encodeURIComponent(kind)}`;
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

function publicPath(companyId, kind, id) {
  const base = `/api/public/companies/${encodeURIComponent(companyId)}/${encodeURIComponent(kind)}`;
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

export async function listCompanyRecords({ companyId, kind, archived, token }) {
  const authToken = token || (await getApiToken());
  const params = new URLSearchParams();
  if (archived != null) params.set('archived', String(archived));
  const query = params.toString();
  const path = `${collectionPath(companyId, kind)}${query ? `?${query}` : ''}`;
  const result = await apiRequest(path, { token: authToken });
  return result[kind] || [];
}

export async function getCompanyRecord({ companyId, kind, id, token }) {
  const authToken = token || (await getApiToken());
  const singular = kind.replace(/s$/, '');
  const result = await apiRequest(collectionPath(companyId, kind, id), { token: authToken });
  return result[singular] || result.client || result.employee || result.service || result.location;
}

export async function upsertCompanyRecord({ companyId, kind, id, record, token }) {
  const authToken = token || (await getApiToken());
  const singular = kind === 'employees' ? 'employee' : kind.replace(/s$/, '');
  const result = await apiRequest(collectionPath(companyId, kind, id), {
    token: authToken,
    method: 'PUT',
    body: { [singular]: toJsonSafe(record) },
  });
  return result[singular] || result.client || result.employee || result.service || result.location;
}

export async function createCompanyRecord({ companyId, kind, record, token }) {
  const authToken = token || (await getApiToken());
  const singular = kind === 'employees' ? 'employee' : kind.replace(/s$/, '');
  const result = await apiRequest(collectionPath(companyId, kind), {
    token: authToken,
    method: 'POST',
    body: { [singular]: toJsonSafe(record) },
  });
  return result[singular] || result.client || result.employee || result.service || result.location;
}

export async function deleteCompanyRecord({ companyId, kind, id, token }) {
  const authToken = token || (await getApiToken());
  return apiRequest(collectionPath(companyId, kind, id), {
    token: authToken,
    method: 'DELETE',
  });
}

export async function listPublicCompanyRecords({ companyId, kind }) {
  const result = await apiRequest(publicPath(companyId, kind));
  return result[kind] || [];
}

export async function getPublicCompanyRecord({ companyId, kind, id }) {
  const singular = kind.replace(/s$/, '');
  const result = await apiRequest(publicPath(companyId, kind, id));
  return result[singular] || result.client || result.employee || result.service || result.location;
}

export async function createPublicClient({ companyId, record }) {
  const result = await apiRequest(publicPath(companyId, 'clients'), {
    method: 'POST',
    body: { client: toJsonSafe(record) },
  });
  return result.client;
}

export async function getPublicClient({ companyId, id }) {
  const result = await apiRequest(publicPath(companyId, 'clients', id));
  return result.client;
}

export async function getPublicClientOrNull({ companyId, id }) {
  try {
    return await getPublicClient({ companyId, id });
  } catch (error) {
    if (error?.status === 404) return null;
    throw error;
  }
}

export async function upsertPublicClient({ companyId, id, record }) {
  const result = await apiRequest(publicPath(companyId, 'clients', id), {
    method: 'PUT',
    body: { client: toJsonSafe(record) },
  });
  return result.client;
}

export async function listCompanyRecordsAuto({ companyId, kind, archived }) {
  const token = await tryGetApiToken();
  if (token) {
    try {
      return await listCompanyRecords({ companyId, kind, archived, token });
    } catch (error) {
      if (error.status !== 401 && error.status !== 403) throw error;
    }
  }
  if (kind === 'clients') {
    throw new Error('Not authorized to list clients');
  }
  return listPublicCompanyRecords({ companyId, kind });
}

export async function getCompanyRecordAuto({ companyId, kind, id }) {
  const token = await tryGetApiToken();
  if (token) {
    try {
      return await getCompanyRecord({ companyId, kind, id, token });
    } catch (error) {
      if (error.status !== 401 && error.status !== 403) throw error;
    }
  }
  if (kind === 'clients') {
    throw new Error('Not authorized to get client');
  }
  return getPublicCompanyRecord({ companyId, kind, id });
}

export const listClients = (opts) => listCompanyRecords({ ...opts, kind: 'clients' });
export const getClient = (opts) => getCompanyRecord({ ...opts, kind: 'clients' });
export const upsertClient = (opts) => upsertCompanyRecord({ ...opts, kind: 'clients', record: opts.client || opts.record });
export const createClient = (opts) => createCompanyRecord({ ...opts, kind: 'clients', record: opts.client || opts.record });
export const deleteClient = (opts) => deleteCompanyRecord({ ...opts, kind: 'clients' });

export const listEmployees = (opts) => listCompanyRecords({ ...opts, kind: 'employees' });
export const getEmployee = (opts) => getCompanyRecord({ ...opts, kind: 'employees' });
export const upsertEmployee = (opts) => upsertCompanyRecord({ ...opts, kind: 'employees', record: opts.employee || opts.record });
export const createEmployee = (opts) => createCompanyRecord({ ...opts, kind: 'employees', record: opts.employee || opts.record });
export const deleteEmployee = (opts) => deleteCompanyRecord({ ...opts, kind: 'employees' });

export const listServices = (opts) => listCompanyRecords({ ...opts, kind: 'services' });
export const getService = (opts) => getCompanyRecord({ ...opts, kind: 'services' });
export const upsertService = (opts) => upsertCompanyRecord({ ...opts, kind: 'services', record: opts.service || opts.record });
export const createService = (opts) => createCompanyRecord({ ...opts, kind: 'services', record: opts.service || opts.record });
export const deleteService = (opts) => deleteCompanyRecord({ ...opts, kind: 'services' });

export const listLocations = (opts) => listCompanyRecords({ ...opts, kind: 'locations' });
export const getLocationRecord = (opts) => getCompanyRecord({ ...opts, kind: 'locations' });
export const upsertLocation = (opts) => upsertCompanyRecord({ ...opts, kind: 'locations', record: opts.location || opts.record });
export const createLocation = (opts) => createCompanyRecord({ ...opts, kind: 'locations', record: opts.location || opts.record });
export const deleteLocation = (opts) => deleteCompanyRecord({ ...opts, kind: 'locations' });
